import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { EApiMiddlewareMethods } from "../../network/networkHandler.types";
import { DriverLocationsState, DriverLocationsResponse, DriverLocationsFilters } from "./DriverLocationsSlice.type";
import { expressDriverBaseURL } from "../../config/Api.config";

import customNetworHandler from "../../network/customNetworHandler";

// Async thunk for fetching driver locations
export const fetchDriverLocations = createAsyncThunk<
  DriverLocationsResponse,
  DriverLocationsFilters,
  { rejectValue: { error: string; message: string } }
>(
  "driverLocations/FetchDriverLocations",
  async (filters, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.hub) queryParams.append('hub', filters.hub);
      if (filters.deliveryType) queryParams.append('deliveryType', filters.deliveryType);
      if (filters.metadata) queryParams.append('metadata', filters.metadata);

      const request = {
        url: `/v2/driver?${queryParams.toString()}`,
        method: EApiMiddlewareMethods.GET
      };

      const { data } = await customNetworHandler(request, expressDriverBaseURL);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { error: 'API_ERROR', message: 'Failed to fetch driver locations' });
    }
  }
);

// Initial state with proper types
const initialState: DriverLocationsState = {
  loading: false,
  errorCode: "",
  error: "",
  // Driver locations data
  driverLocations: [],
  driverLocationsLoading: false,
  driverLocationsError: "",
};

// Slice definition
const DriverLocationsSlice = createSlice({
  name: "driverLocations",
  initialState,
  reducers: {
    clearDriverLocations: (state) => {
      state.driverLocations = [];
      state.driverLocationsError = "";
    }
  },
  extraReducers: (builders) => {
    builders
      .addCase(fetchDriverLocations.pending, (state) => {
        state.driverLocationsLoading = true;
        state.driverLocationsError = "";
      })
      .addCase(fetchDriverLocations.fulfilled, (state, action) => {
        const { payload } = action;
        state.driverLocationsLoading = false;
        if (payload?.results && Array.isArray(payload.results)) {
          state.driverLocations = payload.results;
        }
      })
      .addCase(fetchDriverLocations.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.driverLocationsLoading = false;
        state.driverLocationsError = errorPayload
          ? errorPayload.message || "Failed to fetch driver locations"
          : "Failed to fetch driver locations";
      })
  },
});

export const { clearDriverLocations } = DriverLocationsSlice.actions;
export default DriverLocationsSlice.reducer;
